import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import {
  BackIcon,
  Box,
  Button,
  DialogContentText,
  DialogTitle,
  ForwardIcon,
  Progress as Spinner,
  Stepper
} from "@carrotfertility/carotene-core"
import {
  DialogActionsBar,
  Form,
  FormOnSubmitHandler,
  FormSelect,
  useFormContext
} from "@carrotfertility/carotene-core-x"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useReimbursementContext } from "#/components/context/reimbursement/GetReimbursedContext"
import { FormRadioButtonGroup } from "#/services/common-forms"
import { useDirectPayPrimaryServices } from "#/services/user/hooks/useDirectPayPrimaryServices"
import { getCannotBeSubmittedReason } from "./cannotBeSubmittedReason"
import { useTextDirection } from "#/services/locale/hooks/useTextDirection"
import { ReimbursementDialogFraming } from "../shared/layouts"

const ANOTHER_SERVICE = "ANOTHER_SERVICE"
const NO = "NO"

type FormValues = { relatedPrimaryService: string }

// Question, upload and amount.
const CBPD_PROGRESS_STEPS = 3

function ContinueButton({ enabled }: { enabled: boolean }): JSX.Element {
  const { watch } = useFormContext<FormValues>()
  return (
    <Button
      color="primary"
      endIcon={<ForwardIcon />}
      type="submit"
      disabled={!enabled || !watch("relatedPrimaryService")}
    >
      <FormattedMessage defaultMessage="Continue" />
    </Button>
  )
}

export const RelatedToPrimaryService = (): JSX.Element => {
  const intl = useIntl()
  const { send, back } = useStateMachine()
  const textDirection = useTextDirection()
  const { state, updateRelatedPrimaryService } = useReimbursementContext()
  const { data: services, isError } = useDirectPayPrimaryServices()

  const handleSubmit: FormOnSubmitHandler<FormValues> = ({ relatedPrimaryService }) => {
    const service = services?.find((s) => String(s.expenseId) === relatedPrimaryService) ?? null
    updateRelatedPrimaryService(relatedPrimaryService, service)
    if (!service) {
      send(relatedPrimaryService)
    } else {
      send(
        getCannotBeSubmittedReason(relatedPrimaryService, service)
          ? "PRIMARY_SERVICE_CANNOT_BE_SUBMITTED"
          : "PRIMARY_SERVICE"
      )
    }
  }

  const requiredMessage = intl.formatMessage({ defaultMessage: "Required" })
  // A restored answer can be a service a refetch dropped; treat it as unanswered.
  const isCurrentAnswer = (value: string): boolean =>
    value === ANOTHER_SERVICE || value === NO || !!services?.some((s) => String(s.expenseId) === value)
  const required = {
    rules: { required: requiredMessage, validate: (value: string) => isCurrentAnswer(value) || requiredMessage }
  }

  const serviceOptions = (services ?? []).map((service) => ({
    value: String(service.expenseId),
    label: intl.formatMessage(
      { defaultMessage: "Yes: {serviceName} (Service start date: {serviceStartDate})" },
      {
        serviceName: service.serviceName,
        serviceStartDate: intl.formatDate(`${service.serviceStartDate}T00:00:00Z`, {
          year: "numeric",
          month: "short",
          day: "numeric",
          timeZone: "UTC"
        })
      }
    )
  }))

  // Cached data wins over a failed background refetch.
  let answer: JSX.Element
  if (services && serviceOptions.length === 0) {
    answer = (
      <FormRadioButtonGroup
        aria-labelledby="step-heading"
        name="relatedPrimaryService"
        controllerProps={required}
        options={[
          { label: intl.formatMessage({ defaultMessage: "Yes" }), value: ANOTHER_SERVICE },
          { label: intl.formatMessage({ defaultMessage: "No" }), value: NO }
        ]}
      />
    )
  } else if (services) {
    const options = [
      ...serviceOptions,
      { value: ANOTHER_SERVICE, label: intl.formatMessage({ defaultMessage: "Yes: Another service" }) },
      { value: NO, label: intl.formatMessage({ defaultMessage: "No" }) }
    ]
    const renderValue = (value: unknown): React.ReactNode =>
      options.find((option) => option.value === value)?.label ?? (
        <Box component="span" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          <FormattedMessage defaultMessage="Select" />
        </Box>
      )
    answer = (
      <FormSelect
        aria-labelledby="step-heading"
        name="relatedPrimaryService"
        controllerProps={required}
        options={options}
        renderValue={renderValue}
      />
    )
  } else if (isError) {
    // Never fall back to the Yes/No radios: they'd misroute a member who has services.
    answer = (
      <DialogContentText role="alert" color="error">
        <FormattedMessage defaultMessage="Something went wrong. Please try again." />
      </DialogContentText>
    )
  } else {
    answer = (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Spinner />
      </Box>
    )
  }

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      defaultValues={{
        relatedPrimaryService: state.relatedPrimaryServiceAnswer ?? ""
      }}
    >
      <ReimbursementDialogFraming>
        <Stepper
          dir={textDirection}
          sx={(theme) => ({ marginBlockEnd: theme.spacing(theme.tokens.spacing.xs) })}
          steps={CBPD_PROGRESS_STEPS + 1}
          position="static"
          activeStep={1}
        />
        <DialogTitle id="step-heading">
          <FormattedMessage defaultMessage="Is this expense related to a service scheduled with a Carrot Direct Pay provider?" />
        </DialogTitle>
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(theme.tokens.spacing.lg),
            marginBlockStart: theme.spacing(theme.tokens.spacing.lg)
          })}
        >
          {answer}
        </Box>
      </ReimbursementDialogFraming>
      <DialogActionsBar
        primaryAction={<ContinueButton enabled={!!services} />}
        tertiaryAction={
          <Button variant="text" color="secondary" startIcon={<BackIcon />} onClick={back}>
            <FormattedMessage defaultMessage="Back" />
          </Button>
        }
      />
    </Form>
  )
}
